/**
 * CR225: Single Source of Truth for Card Visibility
 * 
 * This module defines the CANONICAL visibility structure.
 * All editors write to CardVisibility, all renderers read from EffectiveVisibility.
 * 
 * There is NO priority chain - template.visibility IS the source.
 */

import type { MerchantNamePlacement, MerchantNameStyle } from "./globalThemeTokens";

// ================================================================
// CANONICAL VISIBILITY TYPE
// ================================================================

/**
 * The single source of truth for all card visibility toggles.
 * This is stored in template_json.visibility
 * 
 * ALL editors write here. ALL renderers read from here.
 * No syncing, no priority chains, no drift.
 */
export interface CardVisibility {
  // Card Chrome
  showRibbon: boolean;
  showStarburst: boolean;
  
  // Header Elements
  showMerchantLogo: boolean;
  showMerchantName: boolean; // Master toggle - controls ALL merchant name placements
  
  // Body Sections
  showHeadline: boolean;
  showDates: boolean;
  showPromoBadges: boolean;
  showDetailsPills: boolean;
  showMerchantCategoryChips: boolean;
  showConfidenceIndicator: boolean;
  showClaimButton: boolean;
  
  // Debug/Admin
  showDebugStrip: boolean;
  
  // Details Panel Sections
  showEffectiveValueBox: boolean;
  showQualifyingSpendBox: boolean;
  showRewardBox: boolean;
  showEligibilityBox: boolean;
  showSourceButtons: boolean;
  showWhyDealAccordion: boolean;
}

/**
 * Default visibility values - the only place defaults live.
 */
export const DEFAULT_VISIBILITY: CardVisibility = {
  // Card Chrome - ON by default
  showRibbon: true,
  showStarburst: true,
  
  // Header Elements - ON by default
  showMerchantLogo: true,
  showMerchantName: true,
  
  // Body Sections - mostly ON
  showHeadline: true,
  showDates: true,
  showPromoBadges: true,
  showDetailsPills: true,
  showMerchantCategoryChips: true,
  showConfidenceIndicator: false,
  showClaimButton: true,
  
  // Debug - OFF by default
  showDebugStrip: false,
  
  // Details Panel - mixed defaults
  showEffectiveValueBox: false,
  showQualifyingSpendBox: false,
  showRewardBox: false,
  showEligibilityBox: false,
  showSourceButtons: true,
  showWhyDealAccordion: true,
};

// ================================================================
// EFFECTIVE VISIBILITY (computed from CardVisibility + context)
// ================================================================

/**
 * Computed visibility with placement-derived flags.
 * This is what DealGiftCard reads for rendering decisions.
 */
export interface EffectiveVisibility extends CardVisibility {
  // Merchant Name Placement Flags (derived from showMerchantName + placement)
  showMerchantInHeader: boolean;
  showMerchantBodyTop: boolean;
  showMerchantBelowChips: boolean;
  showMerchantAboveDetails: boolean;
  showMerchantFooter: boolean;
  showMerchantInChipsTitle: boolean;
  
  // Theme/Placement Config
  merchantNamePlacement: MerchantNamePlacement;
  merchantNameStyle: MerchantNameStyle;
}

export interface VisibilityContext {
  /** From global theme tokens */
  globalTokens?: {
    components?: {
      merchantName?: {
        placement?: MerchantNamePlacement;
        style?: MerchantNameStyle;
      };
    };
  };
  /** Surface context - may affect defaults */
  surface?: "preview" | "admin" | "public";
  /** Is this an admin context? */
  isAdmin?: boolean;
}

/**
 * Compute effective visibility from the canonical CardVisibility + context.
 * 
 * NO PRIORITY CHAIN - visibility values come directly from CardVisibility.
 * Context only affects placement-derived flags and admin-gating.
 */
export function computeEffectiveVisibility(
  visibility: CardVisibility,
  context: VisibilityContext = {}
): EffectiveVisibility {
  const { globalTokens, isAdmin } = context;
  
  // Get merchant name placement from global tokens
  const merchantNamePlacement: MerchantNamePlacement = 
    globalTokens?.components?.merchantName?.placement ?? "HEADER_TITLE";
  const merchantNameStyle: MerchantNameStyle = 
    globalTokens?.components?.merchantName?.style ?? "TITLE";
  
  // Merchant Name Placements - derived from master toggle + placement
  const showMerchantInHeader = visibility.showMerchantName && merchantNamePlacement === "HEADER_TITLE";
  const showMerchantBodyTop = visibility.showMerchantName && merchantNamePlacement === "BODY_TOP";
  const showMerchantBelowChips = visibility.showMerchantName && merchantNamePlacement === "BODY_BELOW_CHIPS";
  const showMerchantAboveDetails = visibility.showMerchantName && merchantNamePlacement === "BODY_ABOVE_DETAILS";
  const showMerchantFooter = visibility.showMerchantName && merchantNamePlacement === "BODY_FOOTER";
  // Chips row title also respects master toggle
  const showMerchantInChipsTitle = visibility.showMerchantName;
  
  // Debug strip is admin-only
  const showDebugStrip = isAdmin && visibility.showDebugStrip;
  
  return {
    ...visibility,
    showDebugStrip,
    showMerchantInHeader,
    showMerchantBodyTop,
    showMerchantBelowChips,
    showMerchantAboveDetails,
    showMerchantFooter,
    showMerchantInChipsTitle,
    merchantNamePlacement,
    merchantNameStyle,
  };
}

// ================================================================
// MIGRATION HELPERS
// ================================================================

/**
 * Migrate legacy styling/sections to new unified CardVisibility.
 * Used during the transition period.
 */
export function migrateToCardVisibility(
  legacyStyling?: {
    showMerchantLogo?: boolean;
    showMerchantName?: boolean;
    showRibbon?: boolean;
    showStarburst?: boolean;
  },
  legacySections?: {
    merchant_name?: boolean;
    ribbon?: boolean;
    headline?: boolean;
    dates?: boolean;
    promo_badges?: boolean;
    details_pills?: boolean;
    merchant_category_chips?: boolean;
    confidence_indicator?: boolean;
    claim_button?: boolean;
    debug_strip?: boolean;
    effective_value_box?: boolean;
    qualifying_spend_box?: boolean;
    reward_box?: boolean;
    eligibility_box?: boolean;
    source_buttons?: boolean;
    why_deal_accordion?: boolean;
  }
): CardVisibility {
  return {
    // Styling takes precedence for these (legacy priority)
    showRibbon: legacyStyling?.showRibbon ?? legacySections?.ribbon ?? DEFAULT_VISIBILITY.showRibbon,
    showStarburst: legacyStyling?.showStarburst ?? DEFAULT_VISIBILITY.showStarburst,
    showMerchantLogo: legacyStyling?.showMerchantLogo ?? DEFAULT_VISIBILITY.showMerchantLogo,
    showMerchantName: legacyStyling?.showMerchantName ?? legacySections?.merchant_name ?? DEFAULT_VISIBILITY.showMerchantName,
    
    // Sections
    showHeadline: legacySections?.headline ?? DEFAULT_VISIBILITY.showHeadline,
    showDates: legacySections?.dates ?? DEFAULT_VISIBILITY.showDates,
    showPromoBadges: legacySections?.promo_badges ?? DEFAULT_VISIBILITY.showPromoBadges,
    showDetailsPills: legacySections?.details_pills ?? DEFAULT_VISIBILITY.showDetailsPills,
    showMerchantCategoryChips: legacySections?.merchant_category_chips ?? DEFAULT_VISIBILITY.showMerchantCategoryChips,
    showConfidenceIndicator: legacySections?.confidence_indicator ?? DEFAULT_VISIBILITY.showConfidenceIndicator,
    showClaimButton: legacySections?.claim_button ?? DEFAULT_VISIBILITY.showClaimButton,
    showDebugStrip: legacySections?.debug_strip ?? DEFAULT_VISIBILITY.showDebugStrip,
    showEffectiveValueBox: legacySections?.effective_value_box ?? DEFAULT_VISIBILITY.showEffectiveValueBox,
    showQualifyingSpendBox: legacySections?.qualifying_spend_box ?? DEFAULT_VISIBILITY.showQualifyingSpendBox,
    showRewardBox: legacySections?.reward_box ?? DEFAULT_VISIBILITY.showRewardBox,
    showEligibilityBox: legacySections?.eligibility_box ?? DEFAULT_VISIBILITY.showEligibilityBox,
    showSourceButtons: legacySections?.source_buttons ?? DEFAULT_VISIBILITY.showSourceButtons,
    showWhyDealAccordion: legacySections?.why_deal_accordion ?? DEFAULT_VISIBILITY.showWhyDealAccordion,
  };
}

/**
 * Convert CardVisibility back to legacy format for storage compatibility.
 */
export function toLegacyFormat(visibility: CardVisibility): {
  styling: {
    showMerchantLogo: boolean;
    showMerchantName: boolean;
    showRibbon: boolean;
    showStarburst: boolean;
  };
  sections: {
    merchant_name: boolean;
    ribbon: boolean;
    headline: boolean;
    dates: boolean;
    promo_badges: boolean;
    details_pills: boolean;
    merchant_category_chips: boolean;
    confidence_indicator: boolean;
    claim_button: boolean;
    debug_strip: boolean;
    effective_value_box: boolean;
    qualifying_spend_box: boolean;
    reward_box: boolean;
    eligibility_box: boolean;
    source_buttons: boolean;
    why_deal_accordion: boolean;
  };
} {
  return {
    styling: {
      showMerchantLogo: visibility.showMerchantLogo,
      showMerchantName: visibility.showMerchantName,
      showRibbon: visibility.showRibbon,
      showStarburst: visibility.showStarburst,
    },
    sections: {
      merchant_name: visibility.showMerchantName,
      ribbon: visibility.showRibbon,
      headline: visibility.showHeadline,
      dates: visibility.showDates,
      promo_badges: visibility.showPromoBadges,
      details_pills: visibility.showDetailsPills,
      merchant_category_chips: visibility.showMerchantCategoryChips,
      confidence_indicator: visibility.showConfidenceIndicator,
      claim_button: visibility.showClaimButton,
      debug_strip: visibility.showDebugStrip,
      effective_value_box: visibility.showEffectiveValueBox,
      qualifying_spend_box: visibility.showQualifyingSpendBox,
      reward_box: visibility.showRewardBox,
      eligibility_box: visibility.showEligibilityBox,
      source_buttons: visibility.showSourceButtons,
      why_deal_accordion: visibility.showWhyDealAccordion,
    },
  };
}

/**
 * CR225 Addendum: Check if deal has payload for starburst rendering.
 * NOTE: This does NOT affect whether starburst renders - only for debug display.
 * Starburst renders based on visibility.showStarburst ONLY.
 */
export function hasStarburstPayload(deal: {
  effective_percent_back?: number | null;
  computed_value_cad?: number | null;
  bonus_amount?: number | null;
  discount_percent?: number | null;
  percent_back?: number | null;
  points_amount?: number | null;
}): boolean {
  return !!(
    deal.effective_percent_back ||
    deal.computed_value_cad ||
    deal.bonus_amount ||
    deal.discount_percent ||
    deal.percent_back ||
    deal.points_amount
  );
}

/**
 * CR225 Addendum: Check if deal has ribbon payload.
 * NOTE: Ribbon is NOT data-gated - it always renders when visibility.showRibbon = true.
 */
export function hasRibbonPayload(_deal: unknown): boolean {
  // Ribbon is never data-gated - always returns true
  return true;
}

/**
 * CR225 Addendum: Get starburst fallback label when no payload exists.
 */
export function getStarburstFallbackLabel(dealType?: string): string {
  switch (dealType?.toUpperCase()) {
    case "BONUS": return "Bonus";
    case "DISCOUNT": return "Save";
    case "POINTS": return "Points";
    case "IN_KIND": return "Free";
    default: return "Deal";
  }
}
